import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { latLng } from 'leaflet';
import './polygon.scss';
import {
  validateLat,
  validateLon,
} from '../../../utils/validation/coordsValidation';
import { decode } from '../../../utils/flexPolyline';
import { TILE_LIBRARY_TYPES } from '../../../state/map/tiles/constants';
import HereGroup from 'shared/hereMap/HereGroup';

function PolygonList(PolygonComponent, MarkerComponent, renderEngine) {
  return class extends React.Component {
    static propTypes = {
      options: PropTypes.object,
      fields: PropTypes.object,
      setFields: PropTypes.func.isRequired,
      selectedTab: PropTypes.number.isRequired,
      tabIndex: PropTypes.number.isRequired,
    };

    onChangePoint = (geometryIndex, index, pos) => {
      const {
        options: { onChangeFormatter, key, path, mode = 'avoid' },
        setFields,
        fields,
        refClient: { formatterPlugin },
      } = this.props;
      const boundsList = get(this.props, path);
      let newBound = boundsList[geometryIndex];
      const lng = pos.lng>180?180:(pos.lng<-180?-180:pos.lng);
      newBound.geometry[index] = [pos.lat.toFixed(6), lng.toFixed(6)];
      const newBoundsList = [
        ...boundsList.slice(0, geometryIndex),
        newBound,
        ...boundsList.slice(geometryIndex + 1),
      ];

      if (onChangeFormatter) {
        const formatter = formatterPlugin.get(onChangeFormatter);
        setFields(formatter(fields, newBoundsList, mode));
      } else if (key) {
        setFields({ [key]: newBoundsList });
      }
    };

    onChangeMidPoint = (geometryIndex, index, pos) => {
      const {
        options: { onChangeFormatter, key, path, mode = 'avoid' },
        setFields,
        fields,
        refClient: { formatterPlugin },
      } = this.props;
      const boundsList = get(this.props, path);
      let newBound = boundsList[geometryIndex];
      const lng = pos.lng>180?180:(pos.lng<-180?-180:pos.lng);
      newBound.geometry.splice(index, 0, [pos.lat.toFixed(6), lng.toFixed(6)]);
      const newBoundsList = [
        ...boundsList.slice(0, geometryIndex),
        newBound,
        ...boundsList.slice(geometryIndex + 1),
      ];

      if (onChangeFormatter) {
        const formatter = formatterPlugin.get(onChangeFormatter);
        setFields(formatter(fields, newBoundsList, mode));
      } else if (key) {
        setFields({ [key]: newBoundsList });
      }
    };

    onChangeShape = (geometryIndex, shape) =>  {
      const {
        options: { onChangeFormatter, key, path, mode = 'avoid' },
        setFields,
        fields,
        refClient: { formatterPlugin },
      } = this.props;
      const boundsList = get(this.props, path);
      let bound = boundsList[geometryIndex];
      bound.geometry = shape.map(([a, b]) => [(b>90?90:b).toFixed(6), (a>180?180:(a<-180?-180:a)).toFixed(6)]);
      
      if(bound.geometry[0].length > 0){
        const [firstLat, firstLng] = bound.geometry[0];
        const autoName =
          firstLat != null && firstLng != null
            ? `${firstLat}_${firstLng}`
            : bound.name || '';

        if (bound.isAutoName) {
          bound = {
            ...bound,
            name: autoName,
          };
        } 
      }

      const newBoundsList = [
        ...boundsList.slice(0, geometryIndex),
        bound,
        ...boundsList.slice(geometryIndex + 1),
      ];

      if (onChangeFormatter) {
        const formatter = formatterPlugin.get(onChangeFormatter);
        setFields(formatter(fields, newBoundsList, mode));
      } else if (key) {
        setFields({ [key]: newBoundsList });
      }
    }

    onContextMenu = (index, type, event, e) => {
      const { 
        options: { mode = 'avoid', onChangeExcludeFormatter },
        setFields, 
        fields,
        refClient: { formatterPlugin },
      } = this.props;
      const { [mode]: { areas } } = fields;

      if (onChangeExcludeFormatter) {
        if (type === 'remove') {
          const formatter = formatterPlugin.get(onChangeExcludeFormatter+'Remove');
          setFields(formatter(fields, e, mode, index));
        } else if (type === 'bbox' || type === 'polygon' || type === 'corridor'){
          const areaType = type.charAt(0).toUpperCase() + type.slice(1);
          const formatter = formatterPlugin.get(onChangeExcludeFormatter + areaType);
          setFields(formatter(fields, e, mode, true, areas[index].i));
        }
      }
    }

    render() {
      const {
        options: { path, showUnselected, skipCorridor, color, exceptionColor, fillColor, fillOpacity, exceptionFillColor, noException },
        selectedTab,
        tabIndex,
        map
      } = this.props;
      
      if (tabIndex !== selectedTab && !showUnselected) {
        return null;
      }

      const polygons = get(this.props, path, []);
      if(!polygons) return null;
      
      return polygons.map((polygon, geometryIndex) => {
        let { type, isException, geometry } = polygon;
        const markers = [];
        const positions = [];
        let positionsWithMiddle = [];
        let startLat, startLng;
        const draggable = (type !== "encodedPolygon");
        const resizable = (type !== "encodedPolygon");
        let isValid = true;
  
        if (type !== "polygon" && type !== "encodedPolygon") {
          return null;
        }
        if (type === "encodedPolygon") {
          geometry = decode(geometry[0]);
        }

        geometry.forEach(([ lat, lng ], index) => {
          if(lat === null || lng === null || lat === "" || lng === "" || lat === "NaN" || lng === "NaN" || lat === undefined || lng === undefined){
            return null;
          }
          if (validateLat(lat) && validateLon(lng)) {
            const latLngValue = latLng(lat, lng);
            positions.push(latLngValue);
            positionsWithMiddle.push(latLngValue);
            markers.push(
              <MarkerComponent 
                key={index} 
                position={latLngValue} 
                hideOnDrag={true}
                index={index+1} 
                geometryIndex={geometryIndex}
                draggable={draggable} 
                resizable={resizable}
                cursorStyle={'pointer'}
                length={geometry.length}
                onChange={this.onChangePoint.bind(this, geometryIndex, index)}
              />
            );
          } else {
            isValid = false;
          }
        });

        if (draggable || resizable) {
          const length = positions.length;
          let newMiddles = 0;
          positions.forEach(({lat, lng }, index) => {
            if (index > 0) {
              const midlatLngValue = latLng(lat/2 + startLat/2, lng/2 + startLng/2);
              positionsWithMiddle = [
                ...positionsWithMiddle.slice(0, newMiddles+index),
                midlatLngValue,
                ...positionsWithMiddle.slice(newMiddles+index)
              ];
              newMiddles++;
              markers.push(<MarkerComponent 
                key={`middlepoint${index}`}
                position={midlatLngValue}
                hideOnDrag={true}
                index={index}
                geometryIndex={geometryIndex}
                draggable={draggable}
                resizable={resizable}
                middlePoint={true}
                cursorStyle={'pointer'}
                onChange={this.onChangeMidPoint.bind(this, geometryIndex, index)}
              />)
            } else if(length > 2) {
              const lastLat = positions[length-1].lat;
              const lastLng = positions[length-1].lng;
              if (validateLat(lastLat) && validateLon(lastLng)) {
                const midlatLngValue = latLng(lat/2 + lastLat/2, lng/2 + lastLng/2);
                positionsWithMiddle = [
                  ...positionsWithMiddle.slice(0, newMiddles+index),
                  midlatLngValue,
                  ...positionsWithMiddle.slice(newMiddles+index)
                ];
                newMiddles++;
                markers.push(<MarkerComponent 
                  key={`middlepoint${length}`}
                  position={midlatLngValue}
                  hideOnDrag={true}
                  index={length}
                  geometryIndex={geometryIndex}
                  draggable={draggable}
                  resizable={resizable}
                  middlePoint={true}
                  cursorStyle={'pointer'}
                  onChange={this.onChangeMidPoint.bind(this, geometryIndex, length)}
                />)
              }
            }

            startLat = lat;
            startLng = lng;

          })
        }
        if (markers.length === 0) {
          return null;
        }
        const zIndex = 100;
        const interpolatedPositions = [];
        const diff = 0.5;
        const positionsWithMiddleLength = positionsWithMiddle.length;
        let lastPosition = positionsWithMiddle[positionsWithMiddleLength-1];
        for (let i = 0; i<positionsWithMiddleLength; i++) {
          const pos = positionsWithMiddle[i];

          for (let j=1; Math.abs(pos.lat-lastPosition.lat)>=j*diff; j++) {
            const p = Math.abs(pos.lat-lastPosition.lat)/(pos.lat-lastPosition.lat);
            const lat = lastPosition.lat + diff * p * j;
            const dlat = pos.lat - lastPosition.lat;
            const dlng = pos.lng - lastPosition.lng;
            
            let lng = dlng * diff * p * j / dlat + lastPosition.lng;

            interpolatedPositions.push({lat, lng});
          }
          interpolatedPositions.push(pos);
          lastPosition = pos;
        }

        if (renderEngine === TILE_LIBRARY_TYPES.JSLA) {
          return (
              <div key={geometryIndex}>
                <HereGroup options={{ zIndex }} key={geometryIndex}>
                  {isValid && <PolygonComponent 
                  noException={!!noException}
                  contextMenu={true}
                  onContextMenu={this.onContextMenu.bind(this, geometryIndex)}
                  skipCorridor={skipCorridor}
                  map={map}
                  strokeColor={isException ? exceptionColor : color}
                  fillColor={isException ? exceptionFillColor : fillColor}
                  positions={positions}
                  coatPositions={interpolatedPositions}
                  geometryIndex={geometryIndex}
                  draggable={draggable}
                  resizable={resizable}
                  onChange={this.onChangeShape.bind(this, geometryIndex)}
                  isException={isException}
                  />}
                  {markers}
                </HereGroup>
              </div>
          );
        }
        else {
          return (
              <div key={geometryIndex}>
                {isValid && <PolygonComponent 
                noException={!!noException}
                contextMenu={true}
                onContextMenu={this.onContextMenu.bind(this, geometryIndex)}
                skipCorridor={skipCorridor}
                map={map}
                color={isException ? exceptionColor : color}
                fillColor={isException ? exceptionFillColor : fillColor}
                fillOpacity={fillOpacity}
                positions={positions}
                coatPositions={interpolatedPositions}
                showCoatStroke={true}
                geometryIndex={geometryIndex}
                draggable={draggable}
                resizable={resizable}
                onChange={this.onChangeShape.bind(this, geometryIndex)}
                isException={isException}
                />}
                {markers}
              </div>
          );
        }
      });
      
    }
  };
}

export default PolygonList;
